import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCurrentAvailableRates,
  getLowestAvailableStayPayTier,
  getPropertyCombinedRatesByRoom,
  getPropertyMappedBedbankOffer,
} from 'selectors/offerPage/offerPageSelectors'
import Caption from 'LuxKit/Typography/Caption'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import config from 'constants/config'
import ContactContext from 'contexts/contactContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Plural from 'components/utils/Formatters/Plural'
import PropertyRoomOption from './PropertyRoomOption'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { RoomRateViews } from './singlePropertyUtils'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const PackagesLayoutContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(16)};
  }
`

interface Props {
  offer: App.HotelOffer
  bedbankOffer?: App.BedbankOffer
  combinedRatesByRoom: Array<RoomRateViews>
  hasRateError: boolean
  checkIn?: string
  showFlightInformation?: boolean
  destinationAirport?: App.Airport
  onBookNowInvalid?: () => void
  onViewPackageOptions: () => void
  lowestAvailableStayPayTier?: App.StayPayTier | null
}

function PropertyRoomOptions(props: Props) {
  const {
    offer,
    bedbankOffer,
    combinedRatesByRoom,
    hasRateError,
    showFlightInformation,
    destinationAirport,
    onViewPackageOptions,
    checkIn,
    lowestAvailableStayPayTier,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const supportContact = useContext(ContactContext)

  const highlightedPackage = useMemo(
    () => {
      const selectedRoom = combinedRatesByRoom.find(({ isSelected }) => isSelected)
      const highlightedRoom = combinedRatesByRoom.find(({ isHighlighted }) => isHighlighted)
      // If we've selected a different room from the GHA-highlighted room, then suppress GHA
      // highlighting.
      if (!selectedRoom || selectedRoom === highlightedRoom) return highlightedRoom
    },
    [combinedRatesByRoom],
  )

  const roomListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [combinedRatesByRoom]) // regenerate id if list of rooms changes

  // Because we might promote a room with a highlighted rate to the top, we can't use list item
  // indices as the basis for our counter.
  let nextRoomPosition = 1

  return (<VerticalSpacer gap={16}>
    <LayoutContainer>
      {hasRateError && <MessageBanner
        kind="warning"
        tabletAlign="center"
        heading={`The third party booking system for ${offer.property?.name} is currently down`}
      >
        Please call our VIP team on {supportContact.local.humanReadable} who are ready to provide assistance to complete your booking
      </MessageBanner>}

    </LayoutContainer>
    {!hasRateError && <PackagesLayoutContainer>
      <VerticalSpacer gap={32}>
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner
          kind="promotional-primary"
          icon={<LineInfoCircleIcon />}
        >
          <b>Special offer:</b> Enjoy free nights for stays longer than <Plural count={lowestAvailableStayPayTier.stayNights} singular="day" withCount />. Select dates to check eligibility.
        </MessageBanner>}
        {highlightedPackage && !!highlightedPackage.displayRates.length && <PropertyRoomOption
          offer={offer}
          bedbankOffer={bedbankOffer}
          propertyRoom={highlightedPackage.propertyRoom}
          availableRooms={highlightedPackage.availableRooms}
          displayRates={highlightedPackage.displayRates}
          roomFitsCapacity={highlightedPackage.roomFitsCapacity}
          datesAvailable={highlightedPackage.datesAvailable}
          flightDestination={destinationAirport}
          isSelected={highlightedPackage.isSelected}
          isHighlighted
          roomRateUnmatched={highlightedPackage.roomRateUnmatched}
          onViewPackageOptions={onViewPackageOptions}
          roomListId={roomListId}
          roomPosition={nextRoomPosition++}
        />}
        {combinedRatesByRoom.map((item) => {
          if (highlightedPackage && highlightedPackage.roomId === item.roomId) {
            return null
          }
          return (<PropertyRoomOption
            key={item.roomId}
            offer={offer}
            bedbankOffer={bedbankOffer}
            propertyRoom={item.propertyRoom}
            availableRooms={item.availableRooms}
            displayRates={item.displayRates}
            roomFitsCapacity={item.roomFitsCapacity}
            datesAvailable={item.datesAvailable}
            flightDestination={destinationAirport}
            isSelected={item.isSelected}
            onViewPackageOptions={onViewPackageOptions}
            roomListId={roomListId}
            roomPosition={nextRoomPosition++}
          />)
        })}
      </VerticalSpacer>
    </PackagesLayoutContainer>}
    {showFlightInformation && <LayoutContainer>
      <Caption variant="medium" align="center">
        *Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {config.brandConfig.name} full Terms and Conditions apply.
      </Caption>
    </LayoutContainer>}
  </VerticalSpacer>
  )
}

type MappedProps = Pick<Props,
  'offer' |
  'bedbankOffer' |
  'combinedRatesByRoom' |
  'hasRateError' |
  'checkIn' |
  'lowestAvailableStayPayTier'
> & { open?: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  bedbankOffer: getPropertyMappedBedbankOffer(state, appState),
  combinedRatesByRoom: getPropertyCombinedRatesByRoom(state, appState),
  hasRateError: !!getCurrentAvailableRates(state, appState)?.error,
  checkIn: state.checkInDate,
  lowestAvailableStayPayTier: getLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOptions, preventRenderWithOpenFilter)
